import React, { useMemo, useState } from 'react';
import { useSuspense } from 'rest-hooks';
import { EvaluateScoreModel, PolicyCheckResult } from '../../model/entities/scoreModelEvaluation';
import { useApplication } from '../../model/context/applicationContext';
import { Result } from '../../pages/rules/modules/resultEnum';
import { useScoreModel } from '../../model/context/scoreModelContext';
import { EvaluateRunnerResults } from './evaluateRunnerResults';
import { ListEvaluatedResults } from './listEvaluatedResults';
import { ScrollableStack } from '../components/scrollableStack';
import { useFilteredAndSorted } from '../filterSort/useFilteredAndSorted';
import { FilterField } from '../filterSort/filterField';
import { LastRunInfo } from './lastRunInfo';


export const EvaluatedResult = () => {
  const { applicationNumber, applicationXml } = useApplication();
  const { scoreModel } = useScoreModel();

  const evaluationResults = useSuspense(
    EvaluateScoreModel,
    scoreModel && applicationNumber && applicationXml
      ? {
        scoreModelId: scoreModel.scoreModelId,
        applicationNumber: applicationNumber,
        applicationXml: applicationXml
      }
      : null
  );
  const { successList, warningList, failureList } = useMemo(
    () =>
      evaluationResults
        ? evaluationResults.policyCheckResults.reduce<{
          successList: PolicyCheckResult[];
          warningList: PolicyCheckResult[];
          failureList: PolicyCheckResult[];
        }>(
          (acc, result) => {
            switch (result.status) {
              case 'green':
                acc.successList.push(result);
                break;
              case 'yellow':
                acc.warningList.push(result);
                break;
              case 'red':
                acc.failureList.push(result);
                break;
            }
            return acc;
          },
          { successList: [], warningList: [], failureList: [] }
        )
        : { successList: [], warningList: [], failureList: [] },
    [evaluationResults]
  );

  const notRunList = useMemo(
    () =>
      evaluationResults && scoreModel.policyRules
        ? scoreModel.policyRules.filter(
          (rule) =>
            !evaluationResults.policyCheckResults.some(
              (result) => result.policyRuleId === rule.policyRuleId
            )
        )
        : [],
    [evaluationResults]
  );

  const [filteredAndSortedSuccess, setSuccessListFilter] = useFilteredAndSorted(successList);
  const [filteredAndSortedWarning, setWarningListFilter] = useFilteredAndSorted(warningList);
  const [filteredAndSortedFailure, setFailureListFilter] = useFilteredAndSorted(failureList);
  const [filteredAndSortedNotRun, setNotSetListFilter] = useFilteredAndSorted(notRunList);

  const setFilter = (filter: string) => {
    setSuccessListFilter(filter);
    setWarningListFilter(filter);
    setFailureListFilter(filter);
    setNotSetListFilter(filter);
  };
  const lastRunTestDate = evaluationResults ? evaluationResults.evaluationDate : new Date(Date.now());

  const [showError, setShowError] = useState(true);
  const [showWarning, setShowWarning] = useState(true);
  const [showSuccess, setShowSuccess] = useState(true);
  const [showNotRun, setShowNotRun] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState<Result>(Result.NONE);
  const handleFilterTestClick = (resultType: Result, alreadyPressed: boolean) => {
    setShowError(resultType === Result.ERROR || alreadyPressed);
    setShowWarning(resultType === Result.WARNING || alreadyPressed);
    setShowSuccess(resultType === Result.SUCCESS || alreadyPressed);
    setShowNotRun(resultType === Result.NOT_RUN || alreadyPressed);
    setSelectedFilter(alreadyPressed ? Result.NONE : resultType);
  };

  return (
    <>
      <LastRunInfo date={lastRunTestDate}></LastRunInfo>
      <FilterField setFilter={setFilter} />
      <EvaluateRunnerResults
        successes={successList.length}
        warnings={warningList.length}
        failures={failureList.length}
        notRun={notRunList.length}
        evaluationDate={lastRunTestDate}
        handleFilterClick={(resultType, alreadyPressed) => handleFilterTestClick(resultType, alreadyPressed)}
        selectedFilter={selectedFilter}
      />

      {/* Here's a mapping function with the result of all rules */}

      <ScrollableStack spacing={1} >
        {showError && <ListEvaluatedResults evaluations={filteredAndSortedFailure} result={Result.ERROR} />}
        {showWarning && <ListEvaluatedResults evaluations={filteredAndSortedWarning} result={Result.WARNING} />}
        {showSuccess && <ListEvaluatedResults evaluations={filteredAndSortedSuccess} result={Result.SUCCESS} />}
        {showNotRun && <ListEvaluatedResults evaluations={filteredAndSortedNotRun} result={Result.NOT_RUN} />}
      </ScrollableStack>
    </>
  );
};
